/**
 * Action Buttons Component
 * TASK-213: Build Transaction Detail Modal
 *
 * Workflow actions for transaction compliance review
 */

import React, { useState } from 'react';
import { X, Upload, Play, ChevronDown } from 'lucide-react';
import { WorkflowType } from '../../types/compliance';
import { toast } from 'sonner';

interface ActionButtonsProps {
  /** Dismiss handler (undefined if transaction is blocked) */
  onDismiss?: () => void;
  /** Upload document handler */
  onUploadDocument: () => void;
  /** Trigger workflow handler */
  onTriggerWorkflow: (workflowType: WorkflowType) => void;
  /** Whether transaction is blocked */
  isBlocked: boolean;
  /** AI recommendation for suggested workflow */
  aiRecommendation: string;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  onDismiss,
  onUploadDocument,
  onTriggerWorkflow,
  isBlocked,
  aiRecommendation,
}) => {
  const [showWorkflowMenu, setShowWorkflowMenu] = useState(false);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);

  // Workflow options
  const workflowOptions: Array<{ type: WorkflowType; label: string; description: string }> = [
    {
      type: 'EDD_STANDARD',
      label: 'EDD Standard Workflow',
      description: 'Enhanced Due Diligence - Standard procedures',
    },
    {
      type: 'EDD_PEP',
      label: 'EDD PEP Workflow',
      description: 'Enhanced DD for Politically Exposed Persons',
    },
    {
      type: 'LEGAL_ESCALATION',
      label: 'Legal Escalation',
      description: 'Escalate to legal team for SAR assessment',
    },
    {
      type: 'CRITICAL_BLOCK',
      label: 'Critical Block Workflow',
      description: 'Immediate block and regulatory reporting',
    },
  ];

  // Get suggested workflow from AI recommendation
  const getSuggestedWorkflow = (): WorkflowType | null => {
    const recUpper = aiRecommendation.toUpperCase();
    if (recUpper.includes('EDD_PEP')) return 'EDD_PEP';
    if (recUpper.includes('EDD_STANDARD')) return 'EDD_STANDARD';
    if (recUpper.includes('LEGAL_ESCALATION')) return 'LEGAL_ESCALATION';
    if (recUpper.includes('CRITICAL_BLOCK')) return 'CRITICAL_BLOCK';
    return null;
  };

  const suggestedWorkflow = getSuggestedWorkflow();

  // Handle workflow selection
  const handleWorkflowSelect = (workflowType: WorkflowType) => {
    setShowWorkflowMenu(false);
    onTriggerWorkflow(workflowType);
    toast.info(
      `${workflowType} workflow initiated. Remediation workflow modal (TASK-214) coming soon.`
    );
  };

  // Handle upload document
  const handleUploadDocument = () => {
    onUploadDocument();
    toast.info(
      'Document verification modal (TASK-216) coming soon.'
    );
  };

  // Handle dismiss
  const handleDismiss = () => {
    if (showDismissConfirm) {
      onDismiss?.();
      setShowDismissConfirm(false);
    } else {
      setShowDismissConfirm(true);
      // Auto-hide confirmation after 5 seconds
      setTimeout(() => setShowDismissConfirm(false), 5000);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3">
      {/* Dismiss Button (only if not blocked) */}
      {onDismiss && !isBlocked && (
        <button
          onClick={handleDismiss}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            showDismissConfirm
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
          }`}
        >
          {showDismissConfirm ? (
            <span className="flex items-center gap-2">
              <X className="w-4 h-4" />
              Click again to confirm
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <X className="w-4 h-4" />
              Dismiss
            </span>
          )}
        </button>
      )}

      {/* Blocked Status (if blocked) */}
      {isBlocked && (
        <div className="px-4 py-2 bg-red-100 border border-red-300 text-red-800 rounded-lg text-sm font-medium">
          🚫 Transaction Blocked - Cannot Dismiss
        </div>
      )}

      {/* Right Side Actions */}
      <div className="flex items-center gap-3">
        {/* Upload Documents Button */}
        <button
          onClick={handleUploadDocument}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm transition-colors"
        >
          <Upload className="w-4 h-4" />
          Upload Documents
        </button>

        {/* Trigger Workflow Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowWorkflowMenu(!showWorkflowMenu)}
            className="inline-flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm transition-colors"
          >
            <Play className="w-4 h-4" />
            Trigger Workflow
            <ChevronDown className="w-4 h-4" />
          </button>

          {/* Workflow Menu */}
          {showWorkflowMenu && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowWorkflowMenu(false)}
              />

              {/* Menu */}
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-20">
                <div className="p-2">
                  {workflowOptions.map((option) => (
                    <button
                      key={option.type}
                      onClick={() => handleWorkflowSelect(option.type)}
                      className={`w-full text-left px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors ${
                        suggestedWorkflow === option.type
                          ? 'bg-indigo-50 border border-indigo-200'
                          : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {suggestedWorkflow === option.type && (
                          <span className="text-indigo-600 font-bold text-xs mt-0.5">★</span>
                        )}
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900">
                            {option.label}
                            {suggestedWorkflow === option.type && (
                              <span className="ml-2 text-xs text-indigo-600">(Recommended)</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            {option.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActionButtons;
